<template>
  <div class="fwsed-overview-section">
    <content-loading v-if="isOverviewLoading" />
    <div v-else>
      <empty v-if="!hasData">
        <h5 class="text-center">{{ $t("firm.audit.dashboard.no-data") }}</h5>
      </empty>
      <div v-else>
        <div class="row">
          <div class="col-lg-6 mb-3">
            <div class="chart-card">
              <h5 class="card-title">{{ $t("firm.audit.dashboard.status-distribution") }}</h5>
              <apexchart v-if="statusChartHasData" type="pie" height="280" :options="statusChartOptions" :series="statusSeries" />
              <div v-else class="text-muted text-center py-4">
                {{ $t("firm.audit.dashboard.no-data") }}
              </div>
            </div>
          </div>
          <div class="col-lg-6 mb-3">
            <div class="metric-grid metric-grid--vertical">
              <div class="metric-card" v-for="card in metricCards" :key="card.label">
                <div class="metric-label">{{ card.label }}</div>
                <div class="metric-value">{{ card.value }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ContentLoading from "@c/content-loading.vue";
import Empty from "@c/empty.vue";
import ApexChart from "vue-apexcharts";

export default {
  name: "FwsedOverviewSection",
  components: { ContentLoading, Empty, apexchart: ApexChart },
  data() {
    return {
      overviewRequest: {
        pagination: {
          options: { page: 1, limit: 200, sort: { createdAt: "desc" } },
        },
      },
    };
  },
  computed: {
    overview() {
      return this.$store.getters["firmAudit/overview"];
    },
    isOverviewLoading() {
      return this.$store.getters["firmAudit/isOverviewLoading"];
    },
    statusCounts() {
      return this.overview?.statusCounts ?? {};
    },
    statusDistribution() {
      return Object.entries(this.statusCounts).map(([status, count]) => ({
        status,
        count,
        label: this.formatStatus(status),
      }));
    },
    statusSeries() {
      return this.statusDistribution.map((item) => item.count);
    },
    statusChartOptions() {
      return {
        chart: { type: "pie" },
        labels: this.statusDistribution.map((item) => item.label),
        legend: { position: "bottom" },
        colors: ["#4b72fa", "#17a2b8", "#28a745", "#ffc107", "#dc3545", "#6c757d"],
        dataLabels: { enabled: true },
      };
    },
    statusChartHasData() {
      return this.statusSeries.length > 0 && this.statusSeries.reduce((acc, val) => acc + val, 0) > 0;
    },
    metricCards() {
      const averages = this.overview?.averages ?? {};
      const support = this.overview?.supportService ?? {};
      const virtual = this.overview?.virtualServer ?? {};
      const totals = this.overview?.totals ?? {};
      return [
        { label: this.$t("firm.audit.dashboard.audit-count") || "Denetim sayısı", value: totals.audits ?? 0 },
        { label: this.$t("firm.audit.dashboard.question-set-count") || "Soru seti", value: totals.questionSets ?? 0 },
        { label: this.$t("firm.audit.dashboard.avg-test-duration"), value: this.$options.filters.duration(averages.testApproval) },
        { label: this.$t("firm.audit.dashboard.avg-prod-duration"), value: this.$options.filters.duration(averages.prodApproval) },
        { label: this.$t("firm.audit.dashboard.support-yes"), value: support.yes ?? 0 },
        { label: this.$t("firm.audit.dashboard.support-no"), value: support.no ?? 0 },
        { label: this.$t("firm.audit.dashboard.virtual-yes"), value: virtual.yes ?? 0 },
        { label: this.$t("firm.audit.dashboard.virtual-no"), value: virtual.no ?? 0 },
      ];
    },
    hasData() {
      if (!this.overview) {
        return false;
      }
      const totalStatuses = this.statusSeries.reduce((acc, val) => acc + val, 0);
      const totals = this.overview?.totals ?? {};
      const totalFirms = totals.firms ?? 0;
      const totalAudits = totals.audits ?? 0;
      return totalStatuses > 0 || totalFirms > 0 || totalAudits > 0;
    },
  },
  mounted() {
    this.loadOverview();
  },
  methods: {
    async refresh() {
      await this.loadOverview();
    },
    async loadOverview() {
      try {
        await this.$store.dispatch("firmAudit/fetchOverview", this.overviewRequest);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    formatStatus(status) {
      if (!status) {
        return "-";
      }
      const questionSetStatusMap = {
        FIRM_REVIEW: this.$t("firm.audit.status.firm-review"),
        BT_REVIEW: this.$t("firm.audit.status.bt-review"),
        COMPLETED: this.$t("firm.audit.status.completed"),
        IN_PROGRESS: this.$t("firm.audit.status.in-progress"),
        REJECTED: this.$t("firm.audit.status.rejected"),
        NONE: this.$t("firm.audit.status.none"),
      };
      const firmStatusMap = {
        REVIEW: this.$t("firm.status.review"),
        PRE_TEST: this.$t("firm.status.pre-test"),
        TEST: this.$t("firm.status.test"),
        PRE_PROD: this.$t("firm.status.pre-prod"),
        PROD: this.$t("firm.status.prod"),
        CANCELED: this.$t("firm.status.canceled"),
      };
      return firmStatusMap[status] || questionSetStatusMap[status] || status;
    },
    formatDuration(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      if (typeof value === "string") {
        return value;
      }
      if (typeof value === "number") {
        const formatted = value.toLocaleString("tr-TR", { maximumFractionDigits: 1 });
        return `${formatted} saat`;
      }
      return "-";
    },
  },
};
</script>

<style scoped>
.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.metric-grid--vertical {
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
}

.metric-card {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.05);
}

.metric-label {
  font-size: 0.85rem;
  color: #6c757d;
}

.metric-value {
  font-size: 1.6rem;
  font-weight: 600;
  margin-top: 0.35rem;
}

.chart-card {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.05);
  height: 100%;
}

.chart-card .card-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}
</style>
